import { Material } from "three";
import getLogger from '../logging';

const log = getLogger('view3d');


// The MaterialFactory's extension map keys.
// ['Brass', 'Bronze', 'Carpet', 'Chrome', 'FrostedGlass', 'GenericAsset', 'GenericMatte', 'Glass', 'GlossBlack', 'GlossMelamine', 'Herringbone', 'Ivory', 'LeatherOyster', 'LeatherPewter', 'LeatherWinter', 'LightCool', 'LightNeutral', 'LightWarm', 'MatteAluminum', 'MatteBlack', 'MatteGold', 'Mirror', 'Nickel', 'OriginalFlooring', 'Parquet', 'SatinNickel', 'SemiGlossMelamine', 'Slate', 'StandardMelamine', 'WallExtension', 'WallPaint', 'White', 'WindowEnvironment']

// And a directory listing of supported asset ID materials
// antique-copper antique-english antique-nickel black-nickel black-stainless-steel brass bronze brushed-aurum-gold brushed-chrome brushed-nickel brushed-oil-rubbed-bronze burnished-brass champagne-bronze chrome floor-carpet-01 floor-herringbone-01 floor-herringbone-02 floor-herringbone-03 floor-herringbone-04 floor-herringbone-05 floor-parquet-01 floor-wood-01 floor-wood-02 floor-wood-03 floor-wood-04 floor-wood-05 floor-wood-06 floor-wood-07 floor-wood-08 floor-wood-09 floor-wood-10 frosted-glass generic-matte glass gloss-black gloss-melamine honey-bronze ivory leather-oyster leather-pewter leather-winter matte-aluminum matte-black matte-gold matte-nickel metallic-pewter mirror oil-rubbed-bronze polished-copper polished-nickel rose-gold satin-brass satin-nickel-2 satin-nickel semi-gloss-melamine slate standard-melamine wall-paint white

/**
 * This procedure inspects a Material that doesn't have an assetId and tries
 * to convert it to one that does. This exists so that the new render workers
 * can be built without any knowledge of materials that don't have an assetId.
 * @deprecated
 */
export const shimLegacyMaterials = (mat: Material): Record<string, any> => {
    switch(mat.name) {
        case 'Brass':
            return { assetId: 'materials/brass' };
        case 'Bronze':
            return { assetId: 'materials/bronze' };
        case 'Carpet':
            return { assetId: 'materials/floor-carpet-01' };
        case 'Chrome':
            return { assetId: 'materials/chrome' };
        case 'FrostedGlass':
            return { assetId: 'materials/frosted-glass' };

        // GenericAsset omitted---this was used for legacy GenericAssets that
        // are already eliminated from the application
        
        // GenericMatte omitted---I don't think this is used anywhere, but
        // we'll quickly find out if it is.

        case 'Glass':
            return { assetId: 'materials/glass' };
        case 'GlossBlack':
            return { assetId: 'materials/gloss-black' };
        case 'GlossMelamine':
            return { assetId: 'materials/gloss-melamine' };
        case 'Herringbone': {
            switch(mat.userData.materialColormap) {
                case '01A':
                    return { assetId: 'materials/floor-herringbone-01' };
                case '02A':
                    return { assetId: 'materials/floor-herringbone-02' };
                case '02B':
                    return { assetId: 'materials/floor-herringbone-03' };
                case '02C':
                    return { assetId: 'materials/floor-herringbone-04' };
                case '03A':
                    return { assetId: 'materials/floor-herringbone-05' };
                default:
                    log.debug(`Unrecognized Herringbone colormap ${mat.userData.materialColormap}`, mat);
                    return null;
            }
        }
        case 'Ivory':
            return { assetId: 'materials/ivory' };
        case 'LeatherOyster':
            return { assetId: 'materials/leather-oyster' };
        case 'LeatherPewter':
            return { assetId: 'materials/leather-pewter' };
        case 'LeatherWinter':
            return { assetId: 'materials/leather-winter' };
        case 'LightCool':
            return { assetId: 'materials/light-cool' };
        case 'LightNeutral':
            return { assetId: 'materials/light-neutral' };
        case 'LightWarm':
            return { assetId: 'materials/light-warm' };
        case 'MatteAluminum':
            return { assetId: 'materials/matte-aluminum' };
        case 'MatteBlack':
            return { assetId: 'materials/matte-black' };
        case 'MatteGold':
            return { assetId: 'materials/matte-gold' };
        case 'Mirror':
            return { assetId: 'materials/mirror' };
        case 'Nickel':
            return { assetId: 'materials/satin-nickel' };
        case 'OriginalFlooring':
            return { assetId: `materials/floor-wood-${mat.userData.materialColormap}` };
        case 'Parquet':
            return { assetId: 'materials/floor-parquet-01' };
        case 'SatinNickel':
            return { assetId: 'materials/satin-nickel' };
        case 'SemiGlossMelamine':
            return { assetId: 'materials/semi-gloss-melamine' };
        case 'Slate':
            return { assetId: 'materials/slate' };
        case 'StandardMelamine': {
            let color: string = undefined;
            if(mat.userData.materialColormap) {
                const match = mat.userData.materialColormap.match(
                    /closet-organizers-images\/models\/colors\/(\d+)\.png$/);
                if(match) {
                    // Transform the path to the assetId format for the colormap
                    color = `color-maps/${match[1]}`;
                }
            }
            return {
                assetId: 'materials/standard-melamine',
                color,
                tint: mat.userData.materialTint,
            };
        }

        // Omit the WallExtension material, which should not appear in renders

        case 'WallPaint':
            return {
                assetId: 'materials/wall-paint',
                tint: mat.userData.materialTint,
            };
        case 'White':
            return { assetId: 'materials/white' };
        case 'WindowEnvironment':
            return { assetId: 'materials/window-environment-01' };

        default:
            log.debug(`Legacy material "${mat.name}" not supported for asset ID encoding`, mat);
            return null;
    }
}
export default shimLegacyMaterials;
